import { useState, useEffect } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { formatINR } from '@/utils/format';
import { apiClient } from '@/api/client';

export interface HandlePaymentModalState {
  open: boolean;
  returnId: string;
  returnNo: string;
  invoiceId: string;
  supplierId: string;
  supplierName: string;
  amount: number;
  /**
   * Supplier's opening_balance from parties table:
   *   < 0  → you owe the supplier (your outstanding payable)
   *   > 0  → supplier has credit with you (advance / return credit)
   *   = 0  → no outstanding either way
   */
  currentDue: number;
  autoAppliedAmount: number;
  remainingAmount: number;
}

type PaymentMode = 'CASH' | 'UPI' | 'NEFT' | 'RTGS' | 'CHEQUE';

function HandlePaymentModal({
  state,
  onClose,
  onDone,
}: {
  state: HandlePaymentModalState;
  onClose: () => void;
  onDone: (returnId: string, type: 'refund' | 'credit', refundId: string | undefined, settledAmount: number, adjustmentAmount: number) => void;
}) {
  const toast = useToast();
  const [choice, setChoice] = useState<'refund' | 'credit' | null>(null);
  const [refundMode, setRefundMode] = useState<PaymentMode>('CASH');
  const [refundRef, setRefundRef] = useState('');
  const [done, setDone] = useState(false);
  const [finalType, setFinalType] = useState<'refund' | 'credit' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [adjustAgainstDue, setAdjustAgainstDue] = useState(false);

  // ── Balance interpretation ──────────────────────────────────────────
  // currentDue < 0  → we owe the supplier (outstanding payable to them)
  // currentDue >= 0 → supplier already has credit with us
  const weOweSupplier = state.currentDue < 0;
  const ourDebtToSupplier = weOweSupplier ? Math.abs(state.currentDue) : 0;
  const existingCredit = !weOweSupplier ? state.currentDue : 0;  // existing supplier credit balance

  // ── Adjustment (apply return against our debt to supplier) ──────────
  const canAdjust = adjustAgainstDue && weOweSupplier;
  const adjustmentApplied = canAdjust ? Math.min(state.amount, ourDebtToSupplier) : 0;

  // How much of the return value remains after debt adjustment
  const settlementAmount = Math.max(state.amount - adjustmentApplied, 0);

  // Our remaining debt after adjustment
  const newDebtToSupplier = Math.max(ourDebtToSupplier - adjustmentApplied, 0);

  // Fully resolved via adjustment — no remaining amount to settle
  const fullySettled = settlementAmount <= 0;

  // ── Supplier credit balance preview ────────────────────────────────
  // If 'keep as credit' is chosen, the settlement amount is added to supplier's credit balance
  const newSupplierCreditIfCredit = existingCredit + (choice === 'credit' ? settlementAmount : 0);

  useEffect(() => {
    if (state.open) {
      setChoice(null);
      setRefundMode('CASH');
      setRefundRef('');
      setDone(false);
      setFinalType(null);
      setAdjustAgainstDue(false);
    }
  }, [state.open]);

  const handleSimpleSave = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/api/v1/purchase-return/${state.returnId}/handle-payment`, {
        paymentType: 'CREDIT',
        invoiceId: state.invoiceId,
        amount: 0,
        adjustmentAmount: adjustmentApplied,
      });
      toast.success(`Return settled — your payable to ${state.supplierName} reduced by ${formatINR(adjustmentApplied)}`);
      onDone(state.returnId, 'credit', undefined, 0, adjustmentApplied);
      setFinalType('credit');
      setDone(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to settle return');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCredit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/api/v1/purchase-return/${state.returnId}/handle-payment`, {
        paymentType: 'CREDIT',
        invoiceId: state.invoiceId,
        amount: settlementAmount,
        adjustmentAmount: adjustmentApplied,
      });
      toast.success(`${formatINR(settlementAmount)} added to ${state.supplierName}'s credit balance`);
      onDone(state.returnId, 'credit', undefined, settlementAmount, adjustmentApplied);
      setFinalType('credit');
      setDone(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to store credit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefund = async () => {
    if (submitting) return;
    if ((refundMode === 'UPI' || refundMode === 'NEFT' || refundMode === 'RTGS') && !refundRef.trim()) {
      toast.error('Reference number required for this payment mode');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(`/api/v1/purchase-return/${state.returnId}/handle-payment`, {
        paymentType: 'REFUND',
        invoiceId: state.invoiceId,
        amount: settlementAmount,
        adjustmentAmount: adjustmentApplied,
        paymentMode: refundMode,
        referenceNo: refundRef.trim() || undefined,
      });
      toast.success(`Payment of ${formatINR(settlementAmount)} received from ${state.supplierName}`);
      onDone(state.returnId, 'refund', data?.data?.refundId, settlementAmount, adjustmentApplied);
      setFinalType('refund');
      setDone(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to record refund');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Done screen ──────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-2xl w-full max-w-sm p-8 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-4">
            <i className="ri-check-line text-2xl text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-[#1e293b] mb-1">Return Settled!</h3>
          <p className="text-sm text-slate-500 mb-6">
            {finalType === 'credit'
              ? settlementAmount > 0
                ? `${formatINR(settlementAmount)} added to ${state.supplierName}'s credit balance. Will be auto-deducted from next purchase invoice.`
                : `Return value fully adjusted against your outstanding payable.`
              : `Payment of ${formatINR(settlementAmount)} received from ${state.supplierName}.`}
          </p>
          <button onClick={onClose} className="w-full h-10 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  // ── Main modal ───────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-slate-100">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 shrink-0">
            <i className="ri-refund-2-line text-xl text-amber-500" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-[#1e293b]">Settle Purchase Return</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-slate-400">{state.returnNo}</span>
              <span className="text-xs text-slate-300">·</span>
              <span className="text-xs text-slate-400">{state.supplierName}</span>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 pt-4 space-y-4 max-h-[80vh] overflow-y-auto">

          {/* ── Supplier credit balance card ── */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-widest bg-slate-50 flex items-center justify-between">
              <span>Supplier account — {state.supplierName}</span>
            </div>
            <div className="divide-y divide-slate-100">

              {/* Existing credit balance */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <i className="ri-wallet-3-line text-slate-400 text-sm" />
                  <span className="text-sm text-slate-600">Supplier credit balance</span>
                </div>
                <span className={`text-sm font-semibold ${existingCredit > 0 ? 'text-indigo-600' : 'text-slate-400'}`}>
                  {existingCredit > 0 ? `+${formatINR(existingCredit)}` : formatINR(0)}
                </span>
              </div>

              {/* Our outstanding to supplier */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <i className="ri-bill-line text-slate-400 text-sm" />
                  <span className="text-sm text-slate-600">Your payable to supplier</span>
                </div>
                <span className={`text-sm font-semibold ${weOweSupplier ? 'text-rose-500' : 'text-slate-400'}`}>
                  {weOweSupplier ? `−${formatINR(ourDebtToSupplier)}` : formatINR(0)}
                </span>
              </div>

              {/* Return value */}
              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50/60">
                <div className="flex items-center gap-1.5">
                  <i className="ri-arrow-left-down-line text-emerald-600 text-sm" />
                  <span className="text-sm font-semibold text-emerald-700">This return value</span>
                </div>
                <span className="text-sm font-bold text-emerald-700">+{formatINR(state.amount)}</span>
              </div>

              {/* Checkbox: apply against outstanding payable */}
              <div className="flex items-start gap-2.5 px-4 py-3">
                <input
                  id="adjust-purchase-return-due"
                  type="checkbox"
                  disabled={!weOweSupplier}
                  checked={adjustAgainstDue}
                  onChange={(e) => setAdjustAgainstDue(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                />
                <label
                  htmlFor="adjust-purchase-return-due"
                  className={`text-sm leading-snug ${!weOweSupplier ? 'text-slate-400 cursor-not-allowed' : 'text-slate-600 cursor-pointer'}`}
                >
                  {weOweSupplier
                    ? <>Subtract return value from your payable
                      {adjustmentApplied > 0 && <> — <strong className="text-rose-600">reduces your due by {formatINR(adjustmentApplied)}</strong></>}
                    </>
                    : 'No outstanding payable to deduct from'
                  }
                </label>
              </div>

              {/* After-adjustment payable */}
              {weOweSupplier && (
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50/80">
                  <span className="text-xs text-slate-500">Your payable after adjustment</span>
                  <span className={`text-sm font-semibold ${newDebtToSupplier > 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                    {newDebtToSupplier > 0 ? `−${formatINR(newDebtToSupplier)}` : '₹0 (cleared)'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Settlement divider ── */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Settlement</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* ── Fully settled via adjustment ── */}
          {fullySettled && (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <i className="ri-checkbox-circle-fill text-base text-emerald-500 shrink-0" />
              Return value fully absorbed into your payable — nothing more to settle
            </div>
          )}

          {/* ── Remaining amount: supplier must pay or add as credit ── */}
          {!fullySettled && (
            <>
              {/* Amount pending card */}
              <div className="rounded-xl border border-amber-300 bg-amber-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200">
                  <div className="flex items-center gap-2">
                    <i className="ri-arrow-left-down-line text-amber-600 text-base" />
                    <span className="text-sm font-semibold text-amber-700">Supplier still owes you</span>
                  </div>
                  <span className="text-sm font-bold text-amber-700">+{formatINR(settlementAmount)}</span>
                </div>
                <div className="px-4 py-2.5">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    How do you want to record this amount?
                  </p>
                </div>
              </div>

              {/* Choice cards */}
              <div className="grid grid-cols-2 gap-3">

                {/* Option 1: Receive cash/bank payment */}
                <button
                  onClick={() => setChoice('refund')}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${choice === 'refund' ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'
                    }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${choice === 'refund' ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                    <i className={`ri-hand-coin-line text-sm ${choice === 'refund' ? 'text-indigo-600' : 'text-slate-500'}`} />
                  </div>
                  <p className={`text-sm font-semibold ${choice === 'refund' ? 'text-indigo-600' : 'text-slate-700'}`}>
                    Receive payment
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                    Supplier pays {formatINR(settlementAmount)} via cash / bank / UPI
                  </p>
                </button>

                {/* Option 2: Add to supplier credit balance */}
                <button
                  onClick={() => setChoice('credit')}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${choice === 'credit' ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'
                    }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${choice === 'credit' ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                    <i className={`ri-wallet-3-line text-sm ${choice === 'credit' ? 'text-indigo-600' : 'text-slate-500'}`} />
                  </div>
                  <p className={`text-sm font-semibold ${choice === 'credit' ? 'text-indigo-600' : 'text-slate-700'}`}>
                    Add to supplier credit
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                    Store as credit — auto-deducted from next purchase invoice
                  </p>
                </button>
              </div>

              {/* Supplier credit balance preview when "credit" is chosen */}
              {choice === 'credit' && (
                <div className="rounded-xl border border-indigo-200 bg-indigo-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-indigo-100">
                    <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Supplier credit balance update</p>
                  </div>
                  <div className="divide-y divide-indigo-100">
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-indigo-700">Current credit balance</span>
                      <span className="text-xs font-semibold text-indigo-700">{formatINR(existingCredit)}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-indigo-700">Adding from this return</span>
                      <span className="text-xs font-semibold text-emerald-700">+{formatINR(settlementAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-100/60">
                      <span className="text-sm font-semibold text-indigo-800">New credit balance</span>
                      <span className="text-sm font-bold text-indigo-800">{formatINR(newSupplierCreditIfCredit)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment method for refund */}
              {choice === 'refund' && (
                <div className="rounded-xl border border-slate-200 px-4 py-4 space-y-3 bg-white">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Payment details</p>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5">How is supplier paying you?</label>
                    <div className="relative">
                      <select
                        value={refundMode}
                        onChange={(e) => setRefundMode(e.target.value as PaymentMode)}
                        className="w-full h-10 pl-3 pr-8 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-indigo-400 appearance-none cursor-pointer"
                      >
                        <option value="CASH">Cash</option>
                        <option value="NEFT">Bank transfer (NEFT / RTGS)</option>
                        <option value="CHEQUE">Cheque</option>
                        <option value="UPI">UPI</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Reference / note (optional)</label>
                    <input
                      type="text"
                      value={refundRef}
                      onChange={(e) => setRefundRef(e.target.value)}
                      placeholder="e.g. UTR, cheque no., transaction ref"
                      className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 pb-5 border-t border-slate-100 pt-4">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 hover:bg-slate-50 cursor-pointer transition-colors"
          >
            Skip for now
          </button>
          {fullySettled && (
            <button
              onClick={handleSimpleSave}
              disabled={submitting}
              className="flex-1 h-10 rounded-xl bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Confirm'}
            </button>
          )}
          {!fullySettled && choice === 'credit' && (
            <button
              onClick={handleCredit}
              disabled={submitting}
              className="flex-1 h-10 rounded-xl bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving…' : `Add ${formatINR(settlementAmount)} to credit`}
            </button>
          )}
          {!fullySettled && choice === 'refund' && (
            <button
              onClick={handleRefund}
              disabled={submitting}
              className="flex-1 h-10 rounded-xl bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving…' : `Confirm receipt of ${formatINR(settlementAmount)}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default HandlePaymentModal;
